export interface InvoiceLineItem {
  quantity?: number
  unitPrice?: number
  extendedAmountPerItem?: number
}

// qty=0 with no unit price = integration shipping/freight line where the
// charge lives in extendedAmountPerItem (FIRE-1465). qty=0 with a unit
// price is a header/spec line whose canonical amount is 0 (FIRE-1678).
export const lineItemAmount = (li: InvoiceLineItem): number =>
  li.quantity === 0 && !li.unitPrice
    ? li.extendedAmountPerItem ?? 0
    : (li.quantity ?? 0) * (li.unitPrice ?? 0)

export interface InvoiceSummary {
  subtotal: number
  discountShownAsLineItem: boolean
  discountAmountPercent: number
}

// Summary math shared by the partner and payments invoice PDF layouts.
//
// `discountShownAsLineItem` is true only when the negative line items
// reconcile to totalDiscountAmount (FIRE-2344): the discount is already
// itemized in the body, so the summary Discount row would double-count it and
// print an inflated rate (subtotal is net of the discount). A negative line
// that does NOT reconcile — a return credit, deposit, write-off — must never
// suppress the summary, or a non-itemized discount would vanish from the PDF.
export const deriveInvoiceSummary = (
  lineItems: InvoiceLineItem[],
  totalDiscountAmount = 0
): InvoiceSummary => {
  let subtotal = 0
  let negativeLineTotal = 0
  lineItems.forEach(li => {
    const amount = lineItemAmount(li)
    subtotal += amount
    if (amount < 0) negativeLineTotal += amount
  })

  const discountShownAsLineItem =
    totalDiscountAmount > 0 &&
    Math.abs(Math.abs(negativeLineTotal) - totalDiscountAmount) < 0.01

  const discountAmountPercent = subtotal
    ? (totalDiscountAmount * 100) / subtotal
    : 0

  return { subtotal, discountShownAsLineItem, discountAmountPercent }
}

type Part = string | null | undefined

export interface IntegrationBillTo {
  addressLine1?: Part
  addressLine2?: Part
  city?: Part
  stateCode?: Part
  postalCode?: Part
  countryCode?: Part
}

export interface CustomerAddress {
  streetAddress?: Part
  subPremise?: Part
  city?: Part
  state?: Part
  postalCode?: Part
  country?: Part
}

const joinParts = (separator: string, parts: Part[]): string =>
  parts
    .map(part => part?.trim())
    .filter(Boolean)
    .join(separator)

const addressLines = (
  line1: Part,
  line2: Part,
  city: Part,
  state: Part,
  postalCode: Part,
  country: Part
): string[] =>
  [
    line1,
    line2,
    joinParts(', ', [city, joinParts(' ', [state, postalCode])]),
    country
  ]
    .map(line => line?.trim())
    .filter((line): line is string => Boolean(line))

// Lines printed under the customer name next to "Bill" on the invoice PDFs
// (PRD-1831): the invoice's own integration bill-to when it has a first line,
// else the customer's account address. Empty → only the name prints.
// The gateways and svc-email's fallback PDF apply the same rule.
export const billToLines = (
  billTo?: IntegrationBillTo | null,
  customer?: CustomerAddress | null
): string[] => {
  if (billTo?.addressLine1?.trim()) {
    return addressLines(
      billTo.addressLine1,
      billTo.addressLine2,
      billTo.city,
      billTo.stateCode,
      billTo.postalCode,
      billTo.countryCode
    )
  }

  return addressLines(
    customer?.streetAddress,
    customer?.subPremise,
    customer?.city,
    customer?.state,
    customer?.postalCode,
    customer?.country
  )
}
